import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Text, Tabs, Button, Divider, Grid, Row, Col } from 'rsuite';
import dayjs from 'dayjs';
import { TAX_PAYMENT_PERIOD_TYPES_ENUM } from '../../../common/enums';
import ResponsiveModal from '../../ResponsiveModal';
import BasicStyledCheckbox from '../../form/BasicStyledCheckbox';
import { ExtendedPaymentPeriod } from 'src/types/tax';
import { useResize } from 'src/hooks/useResize';

interface AddAdditionalPeriodsModalProps {
  availableReportingPeriods: Array<ExtendedPaymentPeriod>;
  isOpen: boolean;
  onAccept: (periods: Array<ExtendedPaymentPeriod>) => void;
  selectedPeriods: Array<ExtendedPaymentPeriod>;
  setOpen: (isOpen: boolean) => void;
  taxPaymentPeriod: TAX_PAYMENT_PERIOD_TYPES_ENUM;
}

interface SelectedPeriods extends ExtendedPaymentPeriod {
  selected: boolean;
}

const AddAdditionalPeriodsModal = ({
  availableReportingPeriods,
  isOpen,
  onAccept,
  selectedPeriods,
  setOpen,
  taxPaymentPeriod,
}: AddAdditionalPeriodsModalProps) => {
  const { isMobile } = useResize();
  const [localSelectedPeriods, setLocalSelectedPeriods] = useState<
    Array<ExtendedPaymentPeriod>
  >([]);

  useEffect(() => {
    if (isOpen) {
      setLocalSelectedPeriods(selectedPeriods);
    }
  }, [isOpen, selectedPeriods]);

  const closeModal = useCallback(() => setOpen(false), [setOpen]);

  const handleAccept = useCallback(() => {
    onAccept(localSelectedPeriods);
    closeModal();
  }, [closeModal, localSelectedPeriods, onAccept]);

  const handleTogglePeriod = useCallback((period: ExtendedPaymentPeriod) => {
    setLocalSelectedPeriods((prev) => {
      const foundIndex = prev.findIndex(
        (p) => p.startDate === period.startDate
      );
      if (foundIndex === -1) {
        return [...prev, period].sort((a, b) =>
          dayjs(b.startDate).isAfter(dayjs(a.startDate)) ? 1 : -1
        );
      }
      const updated = [...prev];
      updated.splice(foundIndex, 1);
      return updated;
    });
  }, []);

  const years = useMemo(
    () =>
      Array.from(
        new Set(availableReportingPeriods?.map((item) => item.year))
      ).sort((a, b) => b - a),
    [availableReportingPeriods]
  );

  const gridTemplateColumn = useMemo(() => {
    if (
      taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY ||
      taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.NY_FISCAL
    ) {
      return 'repeat(1, 1fr)';
    }
    return isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)';
  }, [taxPaymentPeriod, isMobile]);

  const periodsPerYear = useMemo((): Record<string, Array<SelectedPeriods>> => {
    return [...availableReportingPeriods]
      ?.sort((a, b) =>
        dayjs(b?.startDate).isBefore(dayjs(a?.startDate)) ? 1 : -1
      )
      .reduce(
        (
          acc: Record<string, Array<SelectedPeriods>>,
          period: ExtendedPaymentPeriod
        ) => {
          if (!acc[period.year]) {
            acc[period.year] = [];
          }
          acc[period.year].push({
            ...period,
            selected: localSelectedPeriods.some(
              (p) => p.startDate === period.startDate
            ),
          });
          return acc;
        },
        {}
      );
  }, [availableReportingPeriods, localSelectedPeriods]);

  const gridTemplateRows = useMemo(
    () => (year: number) => {
      const periodsInYear = periodsPerYear[year]?.length || 0;

      if (
        taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY ||
        taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.NY_FISCAL
      ) {
        return `repeat(${Math.ceil(periodsInYear)}, 1fr)`;
      }
      return isMobile
        ? `repeat(${Math.ceil(periodsInYear / 2)}, 1fr)`
        : 'repeat(4, 1fr)';
    },
    [taxPaymentPeriod, isMobile, periodsPerYear]
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={closeModal}
    >
      <Modal.Header>
        <Modal.Title
          as="h2"
          className="text-bold no-padding-modal-section"
        >
          Report Additional Tax
        </Modal.Title>
        <Divider />
      </Modal.Header>
      <Modal.Body>
        <Grid
          fluid
          className="no-padding-modal-section"
        >
          <Row className="mb-12">
            <Col xs={24}>
              <Text className="mb-8">
                Select the periods for which you wish to report tax, including
                reporting $0.00 receipts.
              </Text>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <Tabs
                defaultActiveKey={years[0]?.toString() ?? ''}
                appearance="subtle"
              >
                {years.map((year: number) => (
                  <Tabs.Tab
                    key={`year_tab_${year}`}
                    eventKey={year.toString()}
                    title={year}
                  >
                    <div
                      style={{
                        display: 'inline-grid',
                        gridAutoFlow: 'column',
                        gridTemplateColumns: gridTemplateColumn,
                        gridTemplateRows: gridTemplateRows(year),
                        rowGap: '15px',
                        columnGap: '60px',
                        marginTop: '8px',
                      }}
                    >
                      {periodsPerYear[year]?.map((period) => (
                        <BasicStyledCheckbox
                          key={`period_${period.id}`}
                          checked={period.selected}
                          label={period.name}
                          onChange={() => handleTogglePeriod(period)}
                        />
                      ))}
                    </div>
                  </Tabs.Tab>
                ))}
              </Tabs>
            </Col>
          </Row>
        </Grid>
      </Modal.Body>
      <Modal.Footer>
        <Divider />
        <Grid
          fluid
          className="no-padding-modal-section"
        >
          <Row>
            <Col xs={24}>
              <Button
                type="button"
                className="btn-primary"
                onClick={handleAccept}
              >
                Add Periods
              </Button>
              <Button
                onClick={closeModal}
                appearance="subtle"
                className="btn-link-underline-hover"
                style={{ padding: '8px 12px' }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </Grid>
      </Modal.Footer>
    </ResponsiveModal>
  );
};

export default AddAdditionalPeriodsModal;
